// src/components/RichTextEditor.jsx
// Wiederverwendbarer Tiptap-Rich-Text-Editor. Ursprünglich nur für das
// Research-Antwortfeld gebaut, seit US-PEND-010 (ADR 0058) hierher verschoben
// und für die Pendenzen-Beschreibung (Erfassen/Bearbeiten/Schnellerfassung)
// mitgenutzt.
//
// Hinweis zu Tiptap v3: StarterKit bündelt bereits Underline und Link – diese
// dürfen daher NICHT zusätzlich eingebunden werden (sonst "Duplicate extension").
// TextStyle/Color/TextAlign/Subscript/Superscript sind separat.
//
// Der Editor liest `content` nur beim Mounten ein (Tiptap-Verhalten). Soll er
// nach einem Speichern von aussen geleert werden (z.B. Schnellerfassung-Zeile),
// muss die aufrufende Stelle die Komponente per geändertem `key` neu mounten –
// siehe Pendenzen.jsx `quickAddEditorKey`.

import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { TextAlign } from "@tiptap/extension-text-align";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import RichTextToolbar from "./RichTextToolbar";

const RichTextEditor = forwardRef(
  ({ value, onChange, onBlur, className = "", editable = true, placeholder = "" }, ref) => {
    // Toolbar nur einblenden, während das Feld tatsächlich fokussiert ist
    // (Nutzerwunsch): weniger visuelle Last in Formularen mit mehreren
    // Beschreibungsfeldern (Schnellerfassung, Sitzungs-Präsentation).
    const [isFocused, setIsFocused] = useState(false);
    const editor = useEditor({
      extensions: [
        StarterKit.configure({ link: { openOnClick: false } }),
        TextStyle,
        Color,
        TextAlign.configure({ types: ["heading", "paragraph"] }),
        Subscript,
        Superscript,
      ],
      content: value || "",
      editable,
      immediatelyRender: true,
      // Re-Render bei jeder Transaktion, damit die Toolbar-Active-States UND
      // der Platzhalter (editor.isEmpty) aktuell bleiben.
      shouldRerenderOnTransaction: true,
      onUpdate: ({ editor: ed }) => onChange && onChange(ed.getHTML()),
      onFocus: () => setIsFocused(true),
      onBlur: () => {
        setIsFocused(false);
        onBlur && onBlur();
      },
      editorProps: {
        attributes: { class: "rte-content rte-editable" },
      },
    });

    // `editable` wird von Tiptap nur beim Erstellen gelesen; ein späterer
    // Wechsel (z.B. ein Bearbeiten-Modal, das für read-only-Nutzer:innen
    // gesperrt wird) muss explizit nachgezogen werden – anders als bei
    // <textarea disabled> reicht ein <fieldset disabled> hier NICHT, weil
    // contentEditable nicht Teil des nativen Formular-Deaktivierungsmodells ist.
    useEffect(() => {
      if (editor && editor.isEditable !== editable) {
        editor.setEditable(editable);
      }
    }, [editor, editable]);

    // Erlaubt Aufrufern (z.B. "nächstes Feld fokussieren" nach Verantwortliche-
    // Auswahl), den Editor programmatisch zu fokussieren, ohne den DOM-Knoten
    // selbst zu kennen (contentEditable statt <input>/<textarea>).
    useImperativeHandle(ref, () => ({
      focus: () => editor?.commands.focus("end"),
    }));

    return (
      <div className={`rte${className ? ` ${className}` : ""}${editable ? "" : " rte--readonly"}`}>
        {editable && isFocused && <RichTextToolbar editor={editor} />}
        <div className="rte-body">
          {placeholder && editor?.isEmpty && (
            <span className="rte-placeholder">{placeholder}</span>
          )}
          <EditorContent editor={editor} className="rte-editor-content" />
        </div>
      </div>
    );
  }
);

RichTextEditor.displayName = "RichTextEditor";

export default RichTextEditor;
